/**
 * Wraps a function with an LRU (Least Recently Used) cache, similar to
 * Python's `functools.lru_cache`. The cache key is derived by serializing
 * all arguments with `JSON.stringify`, so arguments must be JSON-serializable.
 *
 * On each call the matching entry is promoted to "most recently used" by
 * deleting and re-inserting it, exploiting the insertion-order guarantee of
 * JavaScript's `Map`. When the cache is full the least recently used entry
 * (the first key in the map) is evicted before the new result is stored.
 *
 * @param fn - The pure function to memoize.
 * @param maxSize - Maximum number of entries to keep (default 128).
 * @returns A wrapped function with the same signature as `fn`.
 *
 * @example
 * function expensiveComputation(input: string): number { ... }
 * const memoized = withLruCache(expensiveComputation, 256);
 */
export function withLruCache<TArgs extends unknown[], TReturn>(
  fn: (...args: TArgs) => TReturn,
  maxSize: number = 128,
): (...args: TArgs) => TReturn {
  const cache = new Map<string, TReturn>();

  return (...args: TArgs): TReturn => {
    const key = JSON.stringify(args);

    const cached = cache.get(key);
    if (cached !== undefined) {
      // Promote to most-recently-used
      cache.delete(key);
      cache.set(key, cached);
      return cached;
    }

    const result = fn(...args);

    if (cache.size >= maxSize) {
      cache.delete(cache.keys().next().value!);
    }
    cache.set(key, result);

    return result;
  };
}
